import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/EngagedMla.png';

// Icons
import {
  LayoutDashboard,
  Users,
  Megaphone,
  Flag,
  CreditCard,
  Settings,
  Menu,
  LogOut,
  CircleUser,
} from 'lucide-react';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { currentUser, userProfile, logout } = useAuth();
  const location = useLocation();

  // Check if device is mobile on component mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
      setSidebarOpen(window.innerWidth >= 768);
    };

    // Initial check
    checkIfMobile();

    // Add event listener
    window.addEventListener('resize', checkIfMobile);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Campaigns', href: '/admin/campaigns', icon: Megaphone },
    { name: 'Reports', href: '/admin/reports', icon: Flag },
    { name: 'Payments', href: '/admin/payments', icon: CreditCard },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  // Flatten for active-item lookup in the header
  const flatNavigation = [...navigation];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } ${
          isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0'
        } bg-white border-r border-gray-200 transition-all duration-300 ease-in-out fixed h-full z-10 flex flex-col`}
      >
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8 border-b border-gray-200 shrink-0">
          {sidebarOpen ? (
            <div className="flex items-center">
              <img src={logo} alt="Engaged Manila Logo" className="h-10 w-auto" />
            </div>
          ) : (
            <div className="flex items-center justify-center mx-auto">
              <img src={logo} alt="Engaged Manila Logo" className="h-8 w-auto" />
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="p-1 rounded-md text-gray-500 hover:text-primary focus:outline-none"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto mt-4">
          <nav className="px-2 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/admin' && location.pathname.startsWith(item.href));

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    isActive
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200`}
                  title={!sidebarOpen ? item.name : undefined}
                >
                  <item.icon
                    className={`${
                      isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600'
                    } ${sidebarOpen ? 'mr-3' : 'mx-auto'} h-6 w-6`}
                    aria-hidden="true"
                  />
                  {sidebarOpen && <span>{item.name}</span>}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* User footer + logout */}
        <div className="border-t border-gray-200 p-3 shrink-0">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              {(() => {
                const photoUrl = userProfile?.logo || userProfile?.photoURL || currentUser?.photoURL;
                const displayName = userProfile?.companyName || userProfile?.displayName || currentUser?.displayName || currentUser?.email || '?';
                if (photoUrl) {
                  return (
                    <img
                      src={photoUrl}
                      alt={displayName}
                      className="h-9 w-9 rounded-full object-cover shrink-0 border border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  );
                }
                return (
                  <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0 border border-gray-200">
                    <CircleUser className="h-7 w-7 text-gray-400" />
                  </div>
                );
              })()}
              {/* Hidden fallback icon shown only if image fails to load */}
              <div className="h-9 w-9 rounded-full bg-gray-100 items-center justify-center shrink-0 border border-gray-200" style={{ display: 'none' }}>
                <CircleUser className="h-7 w-7 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {userProfile?.companyName || userProfile?.displayName || currentUser?.displayName || currentUser?.email}
                </div>
                <div className="text-xs text-gray-500">Admin</div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
                title="Logout"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full group flex items-center justify-center px-2 py-2 text-sm font-medium rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
              title="Logout"
            >
              <LogOut className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className={`flex-1 ${sidebarOpen ? (isMobile ? 'ml-0' : 'ml-64') : 'ml-0 md:ml-20'} transition-all duration-300 ease-in-out`}>
        {/* Mobile header with menu button */}
        {isMobile && (
          <div className="fixed top-0 left-0 right-0 z-20 bg-white shadow-sm h-16 flex items-center px-4">
            <button
              onClick={toggleSidebar}
              className="p-1 rounded-md text-gray-500 hover:text-primary focus:outline-none mr-4"
            >
              <Menu className="h-6 w-6" />
            </button>
            <img src={logo} alt="Engaged Manila Logo" className="h-8 w-auto" />
          </div>
        )}
        <header className={`bg-white shadow-sm ${isMobile ? 'mt-16' : ''}`}>
          <div className="flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <h1 className="text-lg font-semibold text-gray-900">
                {flatNavigation.find(item =>
                  location.pathname === item.href ||
                  (item.href !== '/admin' && location.pathname.startsWith(item.href))
                )?.name || 'Admin Panel'}
              </h1>
              <span className="ml-2 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">Admin</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center gap-2">
                {(() => {
                  const photoUrl = userProfile?.logo || userProfile?.photoURL || currentUser?.photoURL;
                  const displayName = userProfile?.companyName || userProfile?.displayName || currentUser?.displayName || currentUser?.email || '?';
                  if (photoUrl) {
                    return (
                      <img
                        src={photoUrl}
                        alt={displayName}
                        className="h-8 w-8 rounded-full object-cover border border-gray-200"
                      />
                    );
                  }
                  return (
                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                      <CircleUser className="h-6 w-6 text-gray-400" />
                    </div>
                  );
                })()}
                <span className="text-sm font-medium text-gray-700 hidden sm:block">
                  {userProfile?.companyName || userProfile?.displayName || currentUser?.displayName || currentUser?.email}
                </span>
              </div>
            </div>
          </div>
        </header>
        <main className="py-6 px-4 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
